import { signedArea, isSimplePolygon, seededRandom, hashSeed } from './geometry.mjs';

export const SHAPE_HISTORY_KEY = 'perfect-slice.shape-history.v2';
export const HISTORY_LIMIT = 150;
export const SIMILARITY_THRESHOLD = 0.055;
const TAU = Math.PI * 2;
const mix = (a, b, t) => a + (b - a) * t;
const pointMix = (a, b, t) => [mix(a[0], b[0], t), mix(a[1], b[1], t)];
const distance = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const regular = n => Array.from({ length: n }, (_, i) => [Math.cos(i * TAU / n - Math.PI / 2), Math.sin(i * TAU / n - Math.PI / 2)]);
const choose = (random, items) => items[Math.floor(random() * items.length)];
const perturb = (points, random, amount) => points.map(([x, y]) => [x + (random() - .5) * amount, y + (random() - .5) * amount]);

export function familyWeights(round) {
  if (round <= 3) return [['organic', .5], ['basic', .5]];
  if (round <= 8) return [['organic', .34], ['angular', .22], ['basic', .22], ['hybrid', .22]];
  return [['organic', .3], ['angular', .2], ['basic', .2], ['hybrid', .2], ['special', .1]];
}
function selectFamily(random, round) {
  const pick = random(); let running = 0;
  for (const [family, weight] of familyWeights(round)) { running += weight; if (pick < running) return family; }
  return 'special';
}
function smooth(points, passes = 3) {
  let ring = points;
  for (let j = 0; j < passes; j++) ring = ring.flatMap((a, i) => {
    const b = ring[(i + 1) % ring.length]; return [pointMix(a, b, .25), pointMix(a, b, .75)];
  });
  return ring;
}
function mixedCorners(points, radii) {
  // Only selected corners are rounded. Others remain genuinely angular.
  return points.flatMap((vertex, i) => {
    const radius = radii[i] ?? 0;
    if (!radius) return [vertex];
    const before = pointMix(vertex, points[(i + points.length - 1) % points.length], radius);
    const after = pointMix(vertex, points[(i + 1) % points.length], radius);
    return Array.from({ length: 10 }, (_, j) => {
      const t = j / 9, s = 1 - t;
      return [s * s * before[0] + 2 * s * t * vertex[0] + t * t * after[0], s * s * before[1] + 2 * s * t * vertex[1] + t * t * after[1]];
    });
  });
}
function rawShape(random, family, round) {
  const advanced = round >= 9;
  if (family === 'organic') {
    const name = choose(random, round <= 3 ? ['soft-oval', 'bean', 'teardrop'] : ['soft-oval', 'bean', 'teardrop', 'kidney', 'soft-polygon']);
    const templates = {
      'soft-oval': [[-.95,-.35],[-.55,-.95],[.45,-.85],[1,-.1],[.55,.9],[-.6,.75]],
      'bean': [[-.8,-.8],[.3,-1],[1,-.3],[.45,.3],[.45,.95],[-.45,.8],[-1,.15]],
      'teardrop': [[-.2,-1.2],[.8,-.1],[.75,.75],[-.25,1],[-.95,.2]],
      'kidney': [[-.8,-.8],[.35,-1],[.95,-.5],[.3,.05],[.85,.55],[.1,1],[-.9,.5]],
      'soft-polygon': regular(4 + Math.floor(random() * 4)).map(p => p.map(v => v * (.8 + random() * .4)))
    };
    return { name, points: smooth(perturb(templates[name], random, round <= 3 ? .23 : .5)), period: TAU };
  }
  if (family === 'angular') {
    const name = choose(random, ['irregular-triangle', 'quadrilateral', 'kite', 'cut-corner', 'irregular-pentagon', 'uneven-hexagon']);
    const templates = {
      'irregular-triangle': [[-.6,-1],[1,.55],[-1,.75]],
      'quadrilateral': [[-.6,-1],[.95,-.45],[.5,1],[-1,.4]],
      'kite': [[-.1,-1.15],[.85,.2],[.15,1],[-.7,.15]],
      'cut-corner': [[-.95,-.8],[.2,-1],[1,-.25],[.7,.85],[-.95,.55]],
      'irregular-pentagon': regular(5),
      'uneven-hexagon': regular(6)
    };
    return { name, points: perturb(templates[name], random, advanced ? .55 : .32), period: TAU };
  }
  if (family === 'basic') {
    const name = choose(random, ['circle','ellipse','triangle','square','rectangle','trapezoid','pentagon','hexagon','diamond']);
    const templates = {
      circle: regular(96), ellipse: regular(96).map(([x,y]) => [x,y*.65]), triangle: regular(3), square: [[-1,-1],[1,-1],[1,1],[-1,1]], rectangle: [[-1,-.65],[1,-.65],[1,.65],[-1,.65]], trapezoid: [[-.6,-.75],[.65,-.75],[1,.75],[-1,.75]], pentagon: regular(5), hexagon: regular(6), diamond: [[0,-1],[.7,0],[0,1],[-.7,0]]
    };
    const periods = { circle: 0, ellipse: Math.PI, triangle: TAU/3, square: TAU/4, rectangle: Math.PI, trapezoid: TAU, pentagon: TAU/5, hexagon: TAU/6, diamond: Math.PI };
    return { name, points: templates[name], period: periods[name], keepAspect: ['circle','square','triangle','pentagon','hexagon'].includes(name) };
  }
  if (family === 'hybrid') {
    const name = choose(random, ['rounded-triangle','shield','leaf','flat-sided-drop','soft-kite','curved-trapezoid']);
    const templates = {
      'rounded-triangle': { p: [[-.2,-1],[1,.8],[-.95,.6]], r: [.22,.24,.32] },
      shield: { p: [[-.8,-.85],[.85,-.85],[.9,.2],[0,1.05],[-.9,.2]], r: [.32,.32,.18,0,.18] },
      leaf: { p: [[0,-1.2],[.85,-.2],[.55,.65],[-.25,1],[-.8,.1]], r: [0,.8,.65,0,.8] },
      'flat-sided-drop': { p: [[-.3,-1.1],[.9,-.2],[.6,.9],[-.8,.9],[-.85,0]], r: [.1,.65,.08,0,.65] },
      'soft-kite': { p: [[-.2,-1.1],[.95,.1],[.15,1],[-.85,.3]], r: [0,.6,.1,.6] },
      'curved-trapezoid': { p: [[-.55,-.9],[.65,-.75],[1,.8],[-.9,.7]], r: [0,.18,.65,.5] }
    };
    const t = templates[name];
    return { name, points: mixedCorners(perturb(t.p, random, advanced ? .38 : .2), t.r), period: TAU };
  }
  const name = choose(random, ['wide-hourglass','stepped','chevron','corner-notch','long-trapezoid','scooped-pentagon']);
  const templates = {
    'wide-hourglass': [[-1,-.9],[.85,-1],[.52,-.05],[1,.85],[-.85,1],[-.55,.1]],
    stepped: [[-1,-1],[.25,-1],[.25,-.15],[1,-.15],[1,1],[-1,1]],
    chevron: [[-.8,-1],[.95,-.65],[.4,.1],[.8,.95],[-.9,.7],[-.35,-.05]],
    'corner-notch': [[-1,-.95],[1,-.75],[1,.15],[.25,.15],[.25,1],[-.9,.85]],
    'long-trapezoid': [[-.45,-1.3],[.4,-1.1],[.25,1.3],[-.4,1.1]],
    'scooped-pentagon': [[-.9,-.8],[.5,-1],[1,-.25],[.3,.1],[.7,1],[-.75,.8]]
  };
  return { name, points: perturb(templates[name], random, name === 'long-trapezoid' ? .12 : .2), period: TAU };
}
function hull(points) {
  const p = [...points].sort((a,b) => a[0]-b[0] || a[1]-b[1]);
  const cross = (o,a,b) => (a[0]-o[0])*(b[1]-o[1])-(a[1]-o[1])*(b[0]-o[0]);
  const half = input => { const result=[]; for(const point of input) { while(result.length>1 && cross(result.at(-2),result.at(-1),point)<=0) result.pop(); result.push(point); } return result; };
  return [...half(p).slice(0,-1),...half([...p].reverse()).slice(0,-1)];
}
function perimeter(points) { return points.reduce((sum,p,i) => sum + distance(p,points[(i+1)%points.length]),0); }
function scanWidth(points, position) {
  const intersections=[];
  points.forEach((p,i) => { const q=points[(i+1)%points.length]; if((p[0]<=position && q[0]>position)||(q[0]<=position && p[0]>position)) intersections.push(p[1]+(q[1]-p[1])*(position-p[0])/(q[0]-p[0])); });
  intersections.sort((a,b)=>a-b);
  let width=0; for(let i=0;i+1<intersections.length;i+=2) width+=intersections[i+1]-intersections[i];
  return width;
}
export function assessShape(points) {
  if (!isSimplePolygon(points)) return { valid:false, reason:'invalid-polygon' };
  const area = Math.abs(signedArea(points)), length = perimeter(points);
  if (!Number.isFinite(length) || area < 1e-7 || length < 1e-5) return { valid:false, reason:'degenerate' };
  const compactness = 4*Math.PI*area/(length*length), solidity = area/Math.abs(signedArea(hull(points)));
  if (compactness < .22 || solidity < .55) return { valid:false, reason:'extreme-neck-or-sliver' };
  // Scan all orientations: reject a long thin appendage on a broad base, or an
  // interior waist that almost pinches two bodies apart. Curated templates also
  // exclude radial three-lobe/central-stalk constructions by design.
  for(let axis=0;axis<12;axis++) {
    const angle=axis*Math.PI/12, c=Math.cos(angle),s=Math.sin(angle);
    const rotated=points.map(([x,y])=>[x*c-y*s,x*s+y*c]);
    const min=Math.min(...rotated.map(p=>p[0])), max=Math.max(...rotated.map(p=>p[0]));
    const widths=Array.from({length:21},(_,i)=>scanWidth(rotated,min+(max-min)*(.025+i*.0475)));
    const widest=Math.max(...widths);
    if (!(widest>0)) return {valid:false,reason:'degenerate'};
    const w=widths.map(v=>v/widest);
    for(let i=4;i<17;i++) if(w[i]<.23 && Math.max(...w.slice(0,i))>.65 && Math.max(...w.slice(i+1))>.65) return {valid:false,reason:'extreme-neck'};
    for(const profile of [w,[...w].reverse()]) {
      const narrow = profile.slice(1,8);
      const mean = narrow.reduce((a,b)=>a+b,0)/narrow.length;
      if(mean<.34 && Math.max(...narrow)-Math.min(...narrow)<.24 && Math.max(...profile.slice(9))>.8) return {valid:false,reason:'long-protrusion'};
    }
  }
  return { valid:true, compactness, solidity };
}
function transformShape(raw, random) {
  const rotation=random()*TAU, aspect=raw.keepAspect ? 1 : .77+random()*.46;
  const shear = raw.keepAspect || raw.period === 0 || raw.name === 'ellipse' || raw.name === 'rectangle' || raw.name === 'trapezoid' || raw.name === 'diamond' ? 0 : (random()-.5)*.13;
  const c=Math.cos(rotation),s=Math.sin(rotation), scaleVariation=.87+random()*.13;
  const p=raw.points.map(([x,y])=>{ const xx=x*aspect+y*shear; return [xx*c-y*s,xx*s+y*c]; });
  const xs=p.map(v=>v[0]), ys=p.map(v=>v[1]);
  const x0=Math.min(...xs),x1=Math.max(...xs),y0=Math.min(...ys),y1=Math.max(...ys);
  const scale=Math.min(264/(x1-x0),240/(y1-y0))*scaleVariation;
  return { polygon:p.map(([x,y])=>[180+(x-(x0+x1)/2)*scale,160+(y-(y0+y1)/2)*scale]), rotation, aspect, scaleVariation };
}
export function generateShapeDescriptor(seed, round=1) {
  const random=seededRandom(seed), family=selectFamily(random,round);
  for(let attempt=0;attempt<48;attempt++) {
    const raw=rawShape(random,family,round), transformed=transformShape(raw,random);
    const quality=assessShape(transformed.polygon);
    if(quality.valid) return { seed, family, variant:raw.name, period:raw.period, ...transformed, quality };
  }
  // This bounded deterministic rescue is a simple, non-anatomical convex form.
  const raw={ name:family==='basic'?'pentagon':'irregular-pentagon', points:perturb(regular(5),random,.28),period:TAU };
  const transformed=transformShape(raw,random);
  return { seed, family, variant:raw.name, period:raw.period, ...transformed, quality:assessShape(transformed.polygon) };
}
export function generateShape(seed, round=1) { return generateShapeDescriptor(seed,round).polygon; }

function resample(points, count=48) {
  const lengths=points.map((p,i)=>distance(p,points[(i+1)%points.length])), total=lengths.reduce((a,b)=>a+b,0);
  let edge=0,passed=0;
  return Array.from({length:count},(_,i)=>{
    const at=i*total/count;
    while(edge<lengths.length-1 && passed+lengths[edge]<at) { passed+=lengths[edge]; edge++; }
    return pointMix(points[edge],points[(edge+1)%points.length],(at-passed)/(lengths[edge]||1));
  });
}
export function shapeFingerprint(points) {
  const samples=resample(points), n=samples.length;
  const center=samples.reduce((sum,p)=>[sum[0]+p[0]/n,sum[1]+p[1]/n],[0,0]);
  const radii=samples.map(p=>distance(p,center));
  const rms=Math.sqrt(radii.reduce((sum,v)=>sum+v*v,0)/n);
  return {
    radial:radii.map(v=>Number((v/rms).toFixed(4))),
    chord:samples.map((p,i)=>Number((distance(p,samples[(i+8)%n])/rms).toFixed(4))),
    compactness:4*Math.PI*Math.abs(signedArea(points))/perimeter(points)**2,
    solidity:Math.abs(signedArea(points))/Math.abs(signedArea(hull(points)))
  };
}
export function fingerprintDistance(a,b) {
  if(!a?.radial || !b?.radial || a.radial.length!==b.radial.length) return Infinity;
  // Fast exclusion for distinctly different global silhouettes.
  if(Math.abs(a.compactness-b.compactness)>.15 || Math.abs(a.solidity-b.solidity)>.16) return 1;
  const n=a.radial.length; let best=Infinity;
  for(const sign of [1,-1]) for(let shift=0;shift<n;shift++) {
    let sum=0;
    for(let i=0;i<n;i++) {
      const j=(shift+sign*i+n*2)%n;
      const chordIndex=sign===1?j:(j-8+n)%n;
      const r=a.radial[i]-b.radial[j], c=a.chord[i]-b.chord[chordIndex];
      sum+=.65*r*r+.35*c*c;
      if(sum/n>=best) break;
    }
    best=Math.min(best,sum/n);
  }
  return Math.sqrt(best);
}
export function emptyShapeHistory() { return { version:2, procedural:[], basic:[] }; }
function validFingerprint(fp) { return fp && ['radial','chord'].every(key=>Array.isArray(fp[key]) && fp[key].length===48 && fp[key].every(Number.isFinite)) && Number.isFinite(fp.compactness) && Number.isFinite(fp.solidity); }
let memoryHistory=emptyShapeHistory();
export function readShapeHistory(storage) {
  try {
    const source=storage ?? globalThis.localStorage;
    const data=JSON.parse(source?.getItem(SHAPE_HISTORY_KEY)||'null');
    if(data?.version!==2) return memoryHistory;
    return { version:2, procedural:(Array.isArray(data.procedural)?data.procedural:[]).filter(entry=>validFingerprint(entry?.fingerprint)).slice(-HISTORY_LIMIT), basic:(Array.isArray(data.basic)?data.basic:[]).filter(key=>typeof key==='string').slice(-24) };
  } catch { return memoryHistory; }
}
export function saveShapeHistory(history, storage) {
  memoryHistory=history;
  try { (storage ?? globalThis.localStorage)?.setItem(SHAPE_HISTORY_KEY,JSON.stringify(history)); return true; } catch { return false; }
}
function basicKey(shape,target) {
  const bins = shape.period === 0 ? 1 : Math.max(1, Math.round(shape.period / (Math.PI / 12)));
  const angle = shape.period === 0 ? 0 : Math.round((shape.rotation % shape.period) / shape.period * bins) % bins;
  return `${shape.variant}:${angle}:${target}`;
}
export function createChallenge(sessionSeed, round, history=emptyShapeHistory()) {
  const baseSeed=hashSeed(`${sessionSeed}:${round}`), random=seededRandom(baseSeed);
  const earlyTargets=[25,30,35,40,45,55,60,65,70,75];
  const target=round<=3?choose(random,earlyTargets):20+Math.floor(random()*61);
  const family=selectFamily(seededRandom(baseSeed),round);
  let selected=null, furthest=-1, candidates=0;
  for(let nonce=0;nonce<768 && candidates<64;nonce++) {
    const seed=nonce===0?baseSeed:hashSeed(`${sessionSeed}:${round}:variant:${nonce}`);
    if(selectFamily(seededRandom(seed),round)!==family) continue;
    const shape=generateShapeDescriptor(seed,round); candidates++;
    if(shape.family==='basic') {
      if(history.basic.includes(basicKey(shape,target))) continue;
      selected=shape; break;
    }
    const fingerprint=shapeFingerprint(shape.polygon);
    let nearest=Infinity;
    for(const previous of history.procedural) {
      nearest=Math.min(nearest,fingerprintDistance(fingerprint,previous.fingerprint));
      if(nearest<SIMILARITY_THRESHOLD && nearest<=furthest) break;
    }
    if(nearest>furthest) { furthest=nearest; selected={...shape,fingerprint}; }
    if(nearest>=SIMILARITY_THRESHOLD) break;
  }
  // Bounded best-candidate policy avoids hanging a phone with a full history.
  // A geometric basic can always escape its combination cooldown via orientation.
  if(!selected) selected=generateShapeDescriptor(hashSeed(`${baseSeed}:rescue`),round);
  const shape=selected;
  return { ...shape, round,target,colorIndex:Math.floor(random()*5), generationAttempts:candidates, nearestDistance:Number.isFinite(furthest)?furthest:null };
}
export function rememberChallenge(history, challenge) {
  if(challenge.family==='basic') return { ...history,basic:[...history.basic,basicKey(challenge,challenge.target)].slice(-24) };
  return { ...history,procedural:[...history.procedural,{ fingerprint:challenge.fingerprint ?? shapeFingerprint(challenge.polygon),family:challenge.family,seed:challenge.seed }].slice(-HISTORY_LIMIT) };
}
export function createStoredChallenge(sessionSeed, round, storage) {
  const history=readShapeHistory(storage), challenge=createChallenge(sessionSeed,round,history);
  saveShapeHistory(rememberChallenge(history,challenge),storage);
  return challenge;
}
